import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  ageFields,
  getFieldValueOptions,
  getActualStatusNodes,
  enrichFilterOptions,
  filterFieldValuesBySearch,
  fieldsToConvertToBeautifulString,
  getConditionGroup,
  getIsDateField,
  isSetGroup,
  conditionGroupsEnum,
  conditionsEnum,
  fieldTypesEnum,
  filterFieldsEnum,
} from "../../../../../Findings/FindingsFilters/FilterRow/FilterRowUtils";
import { useQuery } from "@apollo/client";
import { GET_FILTER_ROW_VALUES } from "../../../../../Findings/FindingsFilters/FindingsFilterApi";
import { MAX_SEARCH_RESULTS_COUNT } from "../../../../../Findings/FindingsFilters/FilterUtils";
import { flatTree } from "../../../../../Components/Tree/TreeUtils";
import useSeverityAsPriority from "../../../../../General/useSeverityAsPriority";
import { useDebounce } from "react-use";
import {
  Tag,
  useIcon,
  TextInput,
  convertToBeautifulString,
} from "@SeemplicityDev/shared";
import { GeneralAppContext } from "../../../../../General/GeneralAppProvider";
import type { GeneralAppContextType } from "../../../../../types/GeneralAppProvider";
import ValueComponentRenderer, {
  type ValueComponentRendererProps,
} from "./ValueComponentRenderer";
import { widgetKinds, type WidgetKind } from "./widgetKinds";
import type { FieldData } from "../../../../FilterManagement/CommonFilterTypes";
import {
  filterEntityEnum,
  findingFiltersDataContextEnum,
  toCatalogContext,
  UNSET_FILTER_ENTITY_KEY,
  type FilterEntity,
} from "../../../../FilterManagement/filterEntity";
import type { TreeNode } from "../../../../../Components/Tree/types";
import {
  isActualStatusKey,
  isSameRemediationItemFilterKey,
  splitFilterKey,
} from "../../../../FilterManagement/filterKey";
import useAnalystInstanceNames from "../../../../../AnalystRuns/useAnalystInstanceNames";

const MIN_QUERY_LENGTH = 3;
const MIN_NUMERIC_QUERY_LENGTH = 1;

const isNumericValue = (value: unknown): boolean =>
  typeof value === "string" &&
  value.trim() !== "" &&
  !Number.isNaN(Number(value));

const getFilterValuesEntity = (entity: FilterEntity | undefined): string => {
  if (!entity) {
    return UNSET_FILTER_ENTITY_KEY;
  }

  if (entity === filterEntityEnum.SEEMPLICITY_FINDING) {
    return findingFiltersDataContextEnum.SEEMPLICITY_FINDING;
  }

  return toCatalogContext(entity);
};

// A filter value at runtime is polymorphic: a plain string, an array of
// strings, a legacy/saved date object carrying { value, unit }, or an array of
// tree node descriptors (for tree fields like actual_status). Exported via
// FilterSelectionValue because FilterValueSelect / FilterDetailsForm consume it
// as the shared value union.
interface DateValueObject {
  value?: string | number | null;
  unit?: string;
}

export type FilterSelectionValue =
  | string
  | string[]
  | DateValueObject
  | Array<Record<string, unknown>>
  | undefined
  | null;

export interface FilterValueSelectionProps {
  entity?: FilterEntity;
  namePrefix?: string;
  value?: FilterSelectionValue;
  onChange: (value: FilterSelectionValue) => void;
  statuses?: TreeNode[];
  conditionWithValuesList?: boolean;
  fieldsData?: FieldData[];
  fieldValue?: string;
  conditionValue?: string;
  onBlur?: () => void;
  setValid: (isValid: boolean) => void;
}

const FilterValueSelection: React.FC<FilterValueSelectionProps> = ({
  entity,
  namePrefix,
  value,
  onChange,
  statuses,
  conditionWithValuesList,
  fieldsData,
  fieldValue,
  conditionValue,
  onBlur,
  setValid,
}) => {
  // currentTheme is read off the context as it was before the TS migration; it
  // is not declared on GeneralAppContextType (it lived on the untyped JS
  // context), so widen the view here (matching the useFilterDetails reconvert).
  const { currentTheme } = useContext(
    GeneralAppContext
  ) as GeneralAppContextType & {
    currentTheme?: string;
  };
  const [valueSearchInput, setValueSearchInput] = useState("");
  const [debounceValue, setDebounceValue] = useState("");
  const [customValueError, setCustomValueError] = useState(false);
  const { getDataSourceIcon } = useIcon();
  const { severityAsPriorityActive, getSeverityField } =
    useSeverityAsPriority();

  // fieldValue is optional on the props but the FilterRowUtils helpers
  // (ageFields.includes / fieldsToConvertToBeautifulString.includes) expect a
  // string; "" is never a real field, matching the runtime "no match" behaviour
  // of passing undefined.
  const safeFieldValue = fieldValue ?? "";
  const fieldProperty = splitFilterKey(safeFieldValue).property;

  const isAnalystField = fieldProperty === filterFieldsEnum.ANALYST_INSTANCE_ID;
  const isStatusField = isActualStatusKey(fieldValue);
  const { getAnalystDisplayName, isLoading: analystsLoading } =
    useAnalystInstanceNames(isAnalystField);

  const catalogField = useMemo(
    () =>
      fieldsData?.find((fieldData) =>
        isSameRemediationItemFilterKey(fieldData.field, fieldValue)
      ),
    [fieldsData, fieldValue]
  );

  const searchableFilter = useMemo(
    () => catalogField?.searchable || false,
    [catalogField]
  );

  const displayOptionsFilter = useMemo(
    () => catalogField?.display_options || false,
    [catalogField]
  );

  const fieldType = useMemo(
    () => catalogField?.type?.toLowerCase(),
    [catalogField]
  );

  const fieldConditions = useMemo(
    () => catalogField?.conditions || [],
    [catalogField]
  );

  const filterValuesFieldName = catalogField?.field ?? fieldValue;

  const isDateField = useMemo(
    () => getIsDateField(fieldType, fieldConditions),
    [fieldType, fieldConditions]
  );

  const group = useMemo(
    // getConditionGroup requires a string; conditionValue is optional on the
    // props. "" yields the same undefined lookup main's untyped JS produced for
    // a missing condition.
    () => getConditionGroup(conditionValue ?? ""),
    [conditionValue]
  );

  const hasOptions = useMemo(
    () => (!!conditionValue && conditionWithValuesList) || displayOptionsFilter,
    [conditionValue, conditionWithValuesList, displayOptionsFilter]
  );

  useDebounce(
    () => {
      setDebounceValue(valueSearchInput);
    },
    600,
    [valueSearchInput]
  );

  const valueToSearch =
    debounceValue?.length < MIN_QUERY_LENGTH ? "" : debounceValue;

  const baseSkip = !fieldValue || !conditionValue || !conditionWithValuesList;

  const filterValuesEntity = getFilterValuesEntity(entity);

  const { data: baseData, loading: baseLoading } = useQuery(
    GET_FILTER_ROW_VALUES,
    {
      variables: {
        filterName: filterValuesFieldName,
        contains: "",
        limit: MAX_SEARCH_RESULTS_COUNT,
        entity: filterValuesEntity,
        with_total_count: true,
      },
      skip: baseSkip,
    }
  );

  const baseValues = baseData?.finding_filter_data?.values;
  const totalCount = baseData?.finding_filter_data?.total_count;

  const isFullSetLoaded =
    typeof totalCount === "number" && totalCount <= MAX_SEARCH_RESULTS_COUNT;

  const { data: searchData, loading: searchLoading } = useQuery(
    GET_FILTER_ROW_VALUES,
    {
      variables: {
        filterName: filterValuesFieldName,
        contains: valueToSearch,
        limit: MAX_SEARCH_RESULTS_COUNT,
        entity: filterValuesEntity,
      },
      skip: baseSkip || isFullSetLoaded || !valueToSearch,
    }
  );

  const displayedValues = useMemo(() => {
    if (isFullSetLoaded) {
      return filterFieldValuesBySearch(
        fieldValue,
        baseValues,
        valueSearchInput
      );
    }
    if (valueToSearch) {
      return searchData?.finding_filter_data?.values;
    }
    return baseValues;
  }, [
    isFullSetLoaded,
    fieldValue,
    baseValues,
    valueSearchInput,
    valueToSearch,
    searchData,
  ]);

  const loadValues =
    analystsLoading ||
    (isFullSetLoaded
      ? baseLoading
      : valueToSearch
        ? searchLoading
        : baseLoading);

  const textInputType = useMemo(() => {
    if (fieldType === fieldTypesEnum.STRING) {
      return "text";
    } else if (
      fieldType === fieldTypesEnum.NUMBER ||
      fieldType === fieldTypesEnum.INT ||
      fieldType === fieldTypesEnum.FLOAT
    ) {
      return "number";
    }
  }, [fieldType]);

  const valueOptionsCallback = (fieldValue: string) => {
    const property = splitFilterKey(fieldValue).property;
    if (isActualStatusKey(fieldValue)) {
      return { getStatuses: () => statuses };
    } else if (
      property === filterFieldsEnum.SEVERITY ||
      property === filterFieldsEnum.AI_ANALYST_PRIORITY
    ) {
      return {
        severityAsPriorityActive,
        getSeverityField,
      };
    } else if (
      property === filterFieldsEnum.SOURCE ||
      property === filterFieldsEnum.DATASOURCE
    ) {
      return {
        getDataSourceIcon,
      };
    } else if (property === filterFieldsEnum.ANALYST_INSTANCE_ID) {
      return {
        getAnalystDisplayName,
      };
    }

    return null;
  };

  const valueOptions = useMemo(() => {
    let valueOptions =
      getFieldValueOptions(fieldValue, displayedValues, currentTheme) || [];

    return enrichFilterOptions(fieldValue, valueOptions, valueOptionsCallback);
  }, [
    fieldValue,
    displayedValues,
    statuses,
    currentTheme,
    getAnalystDisplayName,
  ]);

  const getPreCheckedNodes = useMemo(() => {
    if (isStatusField) {
      const nodes = getActualStatusNodes(value, statuses);
      const nodesFlatten: TreeNode[] = [];
      nodes.forEach((currNode) => {
        const flat = flatTree(currNode);
        nodesFlatten.push(...flat);
      });

      return nodesFlatten;
    }
  }, [value, isStatusField, statuses]);

  const widget = useMemo(() => {
    const isMembershipOrOverlap = isSetGroup(group);

    let kind: WidgetKind;
    if (group === conditionGroupsEnum.BOOLEAN) {
      kind = widgetKinds.BOOLEAN_RADIOS;
    } else if (isStatusField) {
      kind = widgetKinds.TREE;
    } else if (hasOptions) {
      kind = widgetKinds.CHECKBOX;
    } else if (group === conditionGroupsEnum.LIKELIST) {
      kind = widgetKinds.TAGS;
    } else if (isMembershipOrOverlap && !hasOptions) {
      kind = widgetKinds.TAGS;
    } else if (ageFields?.includes(fieldProperty)) {
      kind = widgetKinds.AGE_RADIOS;
    } else if (group === conditionGroupsEnum.COMPARE && isDateField) {
      kind = widgetKinds.DATE_PICKER;
    } else if (
      isDateField &&
      group !== conditionGroupsEnum.COMPARE &&
      conditionValue !== conditionsEnum.WITHIN
    ) {
      kind = widgetKinds.DATE_RADIOS;
    } else {
      kind = widgetKinds.TEXT_INPUT;
    }

    // For BOOLEAN_RADIOS/TREE/CHECKBOX the wrapper class is not a function of
    // kind alone: an age field, a date field, or a membership/overlap-with-options
    // field can reach these kinds and take a different class. This fallback
    // preserves the original branch order for those cases.
    const dynamicWrapperClass = () => {
      if (ageFields?.includes(fieldProperty)) {
        return "has-age-input";
      } else if (
        isDateField &&
        (group === conditionGroupsEnum.COMPARE ||
          conditionValue !== conditionsEnum.WITHIN)
      ) {
        return "has-date-input";
      } else if (isMembershipOrOverlap && hasOptions) {
        return "has-input";
      } else {
        return "has-other-input";
      }
    };

    const wrapperClassByKind: Record<string, string | (() => string)> = {
      [widgetKinds.TAGS]: "has-title-input",
      [widgetKinds.AGE_RADIOS]: "has-age-input",
      [widgetKinds.DATE_PICKER]: "has-date-input",
      [widgetKinds.DATE_RADIOS]: "has-date-input",
      [widgetKinds.TEXT_INPUT]: "has-other-input",
      [widgetKinds.BOOLEAN_RADIOS]: dynamicWrapperClass,
      [widgetKinds.TREE]: dynamicWrapperClass,
      [widgetKinds.CHECKBOX]: dynamicWrapperClass,
    };

    const wrapperClassEntry = wrapperClassByKind[kind];
    const wrapperClass =
      typeof wrapperClassEntry === "function"
        ? wrapperClassEntry()
        : wrapperClassEntry;

    return { kind, wrapperClass };
  }, [
    conditionValue,
    fieldProperty,
    isStatusField,
    isDateField,
    group,
    hasOptions,
  ]);

  useEffect(() => {
    const dateValue = value as DateValueObject | undefined;
    const minLikeLength = isNumericValue(value)
      ? MIN_NUMERIC_QUERY_LENGTH
      : MIN_QUERY_LENGTH;
    const isValid =
      (Array.isArray(value) ||
      (conditionValue !== conditionsEnum.LIKE &&
        conditionValue !== conditionsEnum.ILIKE)
        ? (value as string | string[])?.length > 0 ||
          (typeof value === "object" && !!dateValue?.value)
        : (value as string | string[])?.length >= minLikeLength) &&
      !customValueError;
    setValid(isValid);
  }, [conditionValue, customValueError, value]);

  // A saved filter can carry a scalar string for a list condition; show it as a
  // single tag. Other non-array shapes (date objects, null) render no tags.
  const selectedTags = useMemo<string[]>(() => {
    if (Array.isArray(value)) {
      return value as string[];
    }
    return typeof value === "string" && value ? [value] : [];
  }, [value]);

  return (
    <div
      className={`${widget.wrapperClass ? widget.wrapperClass : ""}`}
      data-testid={"filter-value-selection-dropdown"}
    >
      {searchableFilter && !!conditionValue && conditionWithValuesList && (
        <>
          <div className={"dropdown-section"}>
            <TextInput
              data-testid={`${namePrefix}.search-value`}
              wrapperClassName={`filter-values-text-box no-padding-bottom`}
              className="menu-search filter-row-last "
              noMargin
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setValueSearchInput(e.target.value);
              }}
              placeholder={"Search"}
              isClearable
              leftIcon={"seem-icon-search"}
              value={valueSearchInput}
              applyAutoFocus
            />
          </div>
          {!!selectedTags.length && (
            <div className={"dropdown-section"}>
              <div className={"dropdown-tag-container-tags"}>
                {selectedTags.map((tag, index) => {
                  return (
                    <Tag
                      chipStyle
                      key={`tag-${index}`}
                      tag={{
                        name: fieldsToConvertToBeautifulString.includes(
                          fieldProperty
                        )
                          ? convertToBeautifulString(tag)
                          : tag,
                      }}
                      onDelete={() => {
                        const data = selectedTags.filter(
                          (item) => item !== tag
                        );
                        onChange(data);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
      <div className={"component-section"}>
        <ValueComponentRenderer
          kind={widget.kind}
          fieldValue={safeFieldValue}
          // value is the broad FilterSelectionValue union; narrow it to the
          // renderer's own value type at this boundary rather than widening the
          // renderer prop.
          value={value as ValueComponentRendererProps["value"]}
          // The renderer emits its FilterEmittedValue union; forward it to the
          // broader FilterSelectionValue onChange (main passed onChange through).
          onChange={(val) => onChange(val as FilterSelectionValue)}
          // ValueComponentRenderer types onBlur as required, but the consumer
          // (FilterValueSelect) passes none — forward main's exact runtime value
          // (possibly undefined) and bridge the optional/required gap here.
          onBlur={onBlur as () => void}
          valueOptions={
            valueOptions as unknown as ValueComponentRendererProps["valueOptions"]
          }
          loadValues={loadValues}
          valueSearchInput={valueSearchInput}
          customValueError={customValueError}
          setCustomValueError={setCustomValueError}
          textInputType={textInputType}
          preCheckedNodes={getPreCheckedNodes}
        />
      </div>
    </div>
  );
};

export default FilterValueSelection;
